import React, { useState } from 'react';
import { Card, Form, Input, Radio, Button, Upload, Select, TreeSelect, Typography, message, Space, Modal } from 'antd';
import { UploadOutlined, SendOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

const { Title } = Typography;
const { TextArea } = Input;
const { Option } = Select;

// ══════════════════════════════════════════════
//  企业提交工单
//  TODO: 联调时替换为接口数据
// ══════════════════════════════════════════════

// 当前企业下所有 App（不过滤审核状态），树形二级：一级=应用名称，二级=系统版本-区域
// 选择后展示格式：App应用名称-系统-区域，如：牵心Pro-iOS-全球
const MOCK_APP_TREE = [
  {
    title: '牵心Pro', value: 'app_qx',
    children: [
      { title: 'iOS-全球', value: 'app_qx-iOS-GL' },
      { title: 'Android-全球', value: 'app_qx-Android-GL' },
    ],
  },
  {
    title: 'HomeStar', value: 'app_hs',
    children: [
      { title: 'iOS-国内', value: 'app_hs-iOS-CN' },
      { title: 'Android-国内', value: 'app_hs-Android-CN' },
      { title: 'iOS-海外', value: 'app_hs-iOS-OV' },
    ],
  },
];

// 当前企业已创建的项目
const MOCK_PROJECTS = [
  { id: 'proj-001', name: 'SDK联调项目', createdAt: '2026-06-01 10:00', updatedAt: '2026-07-10 14:00', deleted: false },
  { id: 'proj-002', name: '私有化部署', createdAt: '2026-06-15 09:00', updatedAt: '2026-07-20 11:00', deleted: false },
];

function TicketCreate() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState([]);
  // 所属项目列表（本页快捷新增后立即可见，无需刷新表单）
  const [projects, setProjects] = useState(MOCK_PROJECTS);
  const [addProjectVisible, setAddProjectVisible] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');

  const handleSubmit = async () => {
    try {
      await form.validateFields();
      setSubmitting(true);
      // 模拟提交
      setTimeout(() => {
        message.success('工单提交成功');
        form.resetFields();
        setFileList([]);
        setSubmitting(false);
        navigate('/customer/ticket/my');
      }, 600);
    } catch {
      // 表单校验失败
    }
  };

  const handleReset = () => {
    form.resetFields();
    setFileList([]);
  };

  // ==================== 快捷添加项目（仅新增） ====================
  const handleAddProject = () => {
    const name = newProjectName.trim();
    if (!name) {
      message.warning('请输入项目名称');
      return;
    }
    const now = new Date().toISOString().replace('T', ' ').slice(0, 16);
    setProjects(prev => [...prev, { id: `proj-${Date.now()}`, name, createdAt: now, updatedAt: now, deleted: false }]);
    setAddProjectVisible(false);
    setNewProjectName('');
    message.success('项目已创建，可在“所属项目”下拉中选择');
  };

  const uploadProps = {
    beforeUpload: (file) => {
      const isValidType = ['image/jpeg', 'image/png', 'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'].includes(file.type);
      if (!isValidType) {
        message.error('仅支持 jpg/png/pdf/docx/xlsx 格式');
        return Upload.LIST_IGNORE;
      }
      if (file.size > 10 * 1024 * 1024) {
        message.error('文件大小不能超过 10MB');
        return Upload.LIST_IGNORE;
      }
      if (fileList.length >= 5) {
        message.warning('最多上传 5 个附件');
        return Upload.LIST_IGNORE;
      }
      setFileList(prev => [...prev, file]);
      return false; // 阻止自动上传
    },
    onRemove: (file) => {
      setFileList(prev => prev.filter(f => f.uid !== file.uid));
    },
    fileList,
  };

  const activeProjects = projects.filter(p => !p.deleted);

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16 }}>
        <Title level={4}>提交工单</Title>
      </div>

      <Card bordered={false} style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', maxWidth: 800 }}>
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item
            name="title"
            label="工单标题"
            rules={[{ required: true, message: '请填写工单标题' }, { max: 100, message: '标题不超过100字' }]}
          >
            <Input placeholder="请输入工单标题，简要描述问题" maxLength={100} showCount />
          </Form.Item>

          <Form.Item
            name="category"
            label="工单分类"
            rules={[{ required: true, message: '请选择工单分类' }]}
          >
            <Radio.Group>
              <Radio.Button value="techSupport">技术对接</Radio.Button>
              <Radio.Button value="bug">BUG</Radio.Button>
              <Radio.Button value="requirement">需求</Radio.Button>
              <Radio.Button value="other">其他</Radio.Button>
            </Radio.Group>
          </Form.Item>

          {/* 所属App：当前企业下所有App（不过滤审核状态），树形二级单选，非必填 */}
          <Form.Item
            name="appValue"
            label="所属App"
            tooltip="当前企业下全部App（含未过审），选择后展示格式：App应用名称-系统-区域"
          >
            <TreeSelect
              treeData={MOCK_APP_TREE}
              placeholder="请选择所属App"
              allowClear
              treeDefaultExpandAll
              style={{ width: 360 }}
            />
          </Form.Item>

          {/* 所属项目：单选下拉 + 快捷添加入口，非必填 */}
          <Form.Item name="projectId" label="所属项目">
            <Space.Compact style={{ width: 360 }}>
              <Select
                placeholder="请选择所属项目"
                allowClear
                style={{ flex: 1 }}
                options={activeProjects.map(p => ({ value: p.id, label: p.name }))}
              />
              <Button icon={<PlusOutlined />} onClick={() => setAddProjectVisible(true)}>
                添加项目
              </Button>
            </Space.Compact>
          </Form.Item>

          <Form.Item
            name="priority"
            label="优先级"
            initialValue="normal"
          >
            <Select style={{ width: 160 }}>
              <Option value="low">低</Option>
              <Option value="normal">普通</Option>
              <Option value="high">高</Option>
              <Option value="urgent">紧急</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="description"
            label="问题描述"
            rules={[
              { required: true, message: '请填写问题描述' },
              { min: 10, message: '问题描述不少于10字' },
            ]}
          >
            <TextArea rows={6} placeholder="请详细描述您遇到的问题，包括复现步骤、期望结果等" maxLength={5000} showCount />
          </Form.Item>

          <Form.Item label="附件">
            <Upload.Dragger {...uploadProps} maxCount={5}>
              <p className="ant-upload-drag-icon">
                <UploadOutlined style={{ fontSize: 36, color: '#1677ff' }} />
              </p>
              <p className="ant-upload-text">点击或拖拽文件上传</p>
              <p className="ant-upload-hint" style={{ color: '#999' }}>
                支持 jpg、png、pdf、docx、xlsx 格式，单文件不超过 10MB，最多 5 个
              </p>
            </Upload.Dragger>
          </Form.Item>

          <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
            <Button type="primary" icon={<SendOutlined />} loading={submitting} onClick={handleSubmit} size="large">
              提交工单
            </Button>
            <Button icon={<DeleteOutlined />} onClick={handleReset} size="large" disabled={submitting}>
              重置
            </Button>
          </div>
        </Form>
      </Card>

      {/* 快捷添加项目弹窗（仅新增，无修改/删除） */}
      <Modal
        title="添加项目"
        open={addProjectVisible}
        onOk={handleAddProject}
        onCancel={() => { setAddProjectVisible(false); setNewProjectName(''); }}
        okText="保存"
        cancelText="取消"
      >
        <Form layout="vertical" requiredMark={false}>
          <Form.Item label="项目名称" required>
            <Input
              placeholder="请输入项目名称"
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              maxLength={50}
              showCount
              onPressEnter={handleAddProject}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}

export default TicketCreate;
